import { getMe } from 'platform-components/src/components/user/selectors';
import {
    withRouterPath,
    InjectedProps as WithRouterPathProps,
    withLocalStorage,
    WithLocalStorageProps,
    InjectedProps as WithRouterPath,
} from 'platform-components/hocs';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import LessonLoader from 'common.components/lesson-loader/lesson-loader';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import { actions as coursesActions } from 'store/courses/actions';
import * as likesSelectors from 'store/likes/selectors';
import { getSearchStringCourseIdOrDefault } from 'utils/get-course';

import { ReduxTeacherStore, RootAction } from '../../../typings';
import * as actions from '../actions';
import * as selectors from '../selectors';

const COMPACT_VIEW_LESSONS_LENGTH = 5;
const COMPACT_VIEW_LESSONS_BREAK_POINT = 7;

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface HocProps {
    classId?: string;
    subject?: string;
}

interface ActiveLessonsProps
    extends StoreProps,
        DispatchProps,
        WithRouterPathProps,
        WithLocalStorageProps,
        WithRouterPath {}

interface State {
    shouldShowAll: boolean;
}

export class ActiveLessons extends React.Component<ActiveLessonsProps, State> {
    state = {
        shouldShowAll: false,
    };

    componentDidUpdate(prevProps: ActiveLessonsProps) {
        const { subject: prevSubject, classId: prevClassId } = prevProps;
        const { subject, classId, clearLessons } = this.props;

        if (subject !== prevSubject || prevClassId !== classId) {
            clearLessons();
        }
    }

    componentWillUnmount(): void {
        const { clearLessons } = this.props;

        clearLessons();
    }

    loadLessons = () => {
        const { courseId } = this.props;

        if (!courseId) {
            return;
        }

        const { getLessonsRequest, getLessonsCountRequest } = this.props;

        getLessonsRequest(courseId, (clessons) => {
            const { getClessonReactions, classId, subject } = this.props;
            getClessonReactions(clessons, classId, subject);
        });
        getLessonsCountRequest({ courseId });
    };

    shouldShowCompactView = () => {
        const { lessons } = this.props;
        const { shouldShowAll } = this.state;

        return !shouldShowAll && lessons.length >= COMPACT_VIEW_LESSONS_BREAK_POINT;
    };

    handleShowAll = () => {
        this.setState({ shouldShowAll: true });
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleLessonClick = (clessonId: number | null) => {
        const { toggleLessonModal } = this.props;

        toggleLessonModal(true);
    };

    render() {
        const { courseId, lessons, loadingStatus, shouldLoadLessons, shouldShowLikes, clessonLikes } = this.props;
        const shouldShowCompactView = this.shouldShowCompactView();

        if (!courseId) {
            return null;
        }

        return (
            <LessonLoader
                canEditLessons={true}
                clessonLikes={clessonLikes}
                compactView={shouldShowCompactView}
                courseId={courseId}
                getNextPageRequest={this.loadLessons}
                lessons={shouldShowCompactView ? lessons.slice(0, COMPACT_VIEW_LESSONS_LENGTH) : lessons}
                lessonsMix="qa_active-lessons"
                loadingStatus={loadingStatus}
                onLessonClick={this.handleLessonClick}
                onShowMore={this.handleShowAll}
                shouldLoadLessons={shouldLoadLessons}
                shouldShowLikes={shouldShowLikes}
            />
        );
    }
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: WithRouterPath & HocProps) => {
    const me = getMe(storeState);
    const classId = ownProps.classId || ownProps.match.params?.classId;
    const subject = ownProps.subject || ownProps.match.params?.subject;

    const currentYearCourseId = getCurrentYearCourseId(storeState, { classId, subject });

    // специальный костыль для КМ-ов, чтобы они могли заходить в любые курсы по get-параметру
    const courseId = me ? getSearchStringCourseIdOrDefault(me, currentYearCourseId) : null;

    return {
        classId: Number(classId),
        subject,
        courseId,
        lessons: selectors.getActiveLessons(storeState, { courseId }),
        loadingStatus: selectors.getActiveLessonsLoadingStatus(storeState, { courseId }),
        shouldShowLikes: likesSelectors.getShouldShowLikeReactions(storeState, {
            classId,
            subject,
        }),
        shouldLoadLessons: selectors.getShouldLoadActiveLessons(storeState, { courseId }),
        me,
        clessonLikes: likesSelectors.getClessonTotalLikes(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => {
    const { clearLessons, getActiveLessonsRequest, getClessonReactionsRequest } = actions;
    const { getLessonsCountRequest } = coursesActions;
    const { toggleModal } = lessonViewActions;

    return bindActionCreators(
        {
            clearLessons,
            getLessonsRequest: getActiveLessonsRequest,
            getLessonsCountRequest,
            toggleLessonModal: toggleModal,
            getClessonReactions: getClessonReactionsRequest,
        },
        dispatch
    );
};

export default withRouterPath(withLocalStorage(connect(mapStateToProps, mapDispatchToProps)(ActiveLessons)));
